import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/RegionContext';
import { useRegion } from '@/context/RegionContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SEO from '@/components/SEO';
import { CreditCard, MapPin, User, ArrowRight, Truck, Tag, X as XIcon, LogIn, UserCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

const Checkout = () => {
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { formatPrice } = useCurrency();
  const { isIndia, region } = useRegion();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [guestCheckout, setGuestCheckout] = useState(false);

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    country: isIndia ? 'India' : ''
  });

  // Pre-fill form when user logs in
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_email: user.email || prev.customer_email,
        customer_phone: user.phone || prev.customer_phone,
        address_line1: user.address?.address_line1 || prev.address_line1,
        address_line2: user.address?.address_line2 || prev.address_line2,
        city: user.address?.city || prev.city,
        state: user.address?.state || prev.state,
        pincode: user.address?.pincode || prev.pincode,
        country: user.address?.country || prev.country,
      }));
    }
  }, [user]);

  const shippingCost = formData.country === 'India' ? 500 : 2500;
  const subtotal = getCartTotal();
  const discountAmount = appliedDiscount ? appliedDiscount.discount_amount : 0;
  const total = subtotal - discountAmount + shippingCost;

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    setApplyingDiscount(true);
    try {
      const res = await axios.post(`${API}/discounts/validate`, { code: discountCode.trim(), subtotal });
      setAppliedDiscount(res.data);
      toast.success(`Discount applied: ${res.data.label}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid discount code');
      setAppliedDiscount(null);
    } finally { setApplyingDiscount(false); }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsProcessing(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        setIsProcessing(false);
        return;
      }

      const orderData = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        shipping_address: {
          address_line1: formData.address_line1,
          address_line2: formData.address_line2,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          country: formData.country
        },
        shipping_country: formData.country,
        items: cartItems.map(item => ({
          artwork_id: item.id,
          title: item.title,
          price: item.price,
          quantity: item.quantity,
          image_url: item.image_url
        })),
        discount_code: appliedDiscount?.code || '',
      };

      const response = await axios.post(`${API}/orders/create`, orderData);
      const { razorpay_order_id, amount, key_id } = response.data;

      const options = {
        key: key_id || RAZORPAY_KEY,
        amount: amount * 100,
        currency: 'INR',
        name: 'Gangadhar Som',
        description: 'Artwork Purchase',
        order_id: razorpay_order_id,
        handler: async function (resp) {
          try {
            const verifyResponse = await axios.post(`${API}/orders/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature
            });

            if (verifyResponse.data.success) {
              clearCart();
              toast.success('Payment successful! Order confirmed.');
              navigate('/gallery');
            } else {
              toast.error('Payment verification failed');
            }
          } catch (error) {
            console.error('Verification error:', error);
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: formData.customer_name,
          email: formData.customer_email,
          contact: formData.customer_phone
        },
        notes: {
          address: `${formData.address_line1}, ${formData.city}`
        },
        theme: {
          color: '#D97706'
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
            toast.error('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment');
      setIsProcessing(false);
    }
  };

  if (cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="checkout-page">
      <SEO title="Checkout - Gangadhar Som" noindex />
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mb-8 sm:mb-12">
          Checkout
        </h1>

        {/* Login / Guest Gate */}
        {!user && !guestCheckout ? (
          <div className="max-w-lg mx-auto space-y-4 mb-12" data-testid="checkout-auth-gate">
            <div className="bg-card rounded-lg border border-border p-6 sm:p-8 text-center space-y-5">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-accent/10">
                <UserCircle size={28} className="text-accent" />
              </div>
              <div>
                <h2 className="font-serif text-xl sm:text-2xl font-light">How would you like to checkout?</h2>
                <p className="text-sm text-muted-foreground mt-1.5">
                  Sign in to auto-fill your details and track orders, or continue as a guest
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  onClick={login}
                  className="flex-1 bg-accent hover:bg-accent/90 h-12"
                  data-testid="checkout-login-btn"
                >
                  <LogIn size={16} className="mr-2" />
                  Sign in with Google
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setGuestCheckout(true)}
                  className="flex-1 h-12 border-border"
                  data-testid="checkout-guest-btn"
                >
                  <User size={16} className="mr-2" />
                  Continue as Guest
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
          {user && (
            <div className="flex items-center gap-2 mb-6 text-sm text-muted-foreground" data-testid="checkout-user-info">
              <UserCircle size={16} className="text-accent" />
              <span>Checking out as <strong className="text-foreground">{user.name || user.email}</strong></span>
            </div>
          )}

        <form onSubmit={handlePayment}>
          <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Checkout Form */}
            <div className="lg:col-span-2 space-y-8">
              {/* Customer Information */}
              <div className="bg-card rounded-lg p-4 sm:p-6 border border-border">
                <h2 className="font-serif text-xl sm:text-2xl font-light mb-4 sm:mb-6 flex items-center gap-2">
                  <User size={22} />
                  Customer Information
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="customer_name">Full Name *</Label>
                    <Input
                      id="customer_name"
                      name="customer_name"
                      data-testid="checkout-name-input"
                      value={formData.customer_name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label htmlFor="customer_email">Email *</Label>
                    <Input
                      id="customer_email"
                      name="customer_email"
                      type="email"
                      data-testid="checkout-email-input"
                      value={formData.customer_email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="customer_phone">Phone Number *</Label>
                    <Input
                      id="customer_phone"
                      name="customer_phone"
                      type="tel"
                      data-testid="checkout-phone-input"
                      value={formData.customer_phone}
                      onChange={handleChange}
                      required
                      placeholder="+91 1234567890"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-card rounded-lg p-4 sm:p-6 border border-border">
                <h2 className="font-serif text-xl sm:text-2xl font-light mb-4 sm:mb-6 flex items-center gap-2">
                  <MapPin size={22} />
                  Shipping Address
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="address_line1">Address Line 1 *</Label>
                    <Input
                      id="address_line1"
                      name="address_line1"
                      data-testid="checkout-address-input"
                      value={formData.address_line1}
                      onChange={handleChange}
                      required
                      placeholder="Street address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address_line2">Address Line 2</Label>
                    <Input
                      id="address_line2"
                      name="address_line2"
                      value={formData.address_line2}
                      onChange={handleChange}
                      placeholder="Apartment, suite, etc."
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        data-testid="checkout-city-input"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        placeholder="Bangalore"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        name="state"
                        data-testid="checkout-state-input"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        placeholder="Karnataka"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pincode">PIN Code *</Label>
                      <Input
                        id="pincode"
                        name="pincode"
                        data-testid="checkout-pincode-input"
                        value={formData.pincode}
                        onChange={handleChange}
                        required
                        placeholder="560001"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country *</Label>
                      {isIndia ? (
                        <Input id="country" name="country" value="India" disabled
                          className="bg-muted/50" data-testid="checkout-country-select" />
                      ) : (
                        <select
                          id="country" name="country"
                          data-testid="checkout-country-select"
                          value={formData.country}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg font-sans text-base focus:outline-none focus:ring-2 focus:ring-accent"
                        >
                          <option value="">Select country...</option>
                          <option value="USA">USA</option>
                          <option value="UK">UK</option>
                          <option value="Canada">Canada</option>
                          <option value="Australia">Australia</option>
                          <option value="Germany">Germany</option>
                          <option value="France">France</option>
                          <option value="Japan">Japan</option>
                          <option value="Other">Other</option>
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-card rounded-lg p-4 sm:p-6 border border-border lg:sticky lg:top-24">
                <h2 className="font-serif text-xl sm:text-2xl font-light mb-4 sm:mb-6">Order Summary</h2>

                <div className="space-y-4 mb-6 max-h-60 overflow-y-auto">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex gap-3" data-testid={`checkout-item-${item.id}`}>
                      <img src={item.image_url} alt={item.title} className="w-16 h-16 object-cover rounded" />
                      <div className="flex-1">
                        <p className="font-sans text-sm font-medium">{item.title}</p>
                        <p className="font-sans text-xs text-muted-foreground">Qty: {item.quantity}</p>
                        <p className="font-sans text-sm font-medium">{formatPrice(item.price * item.quantity)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 border-t pt-4">
                  <div className="flex justify-between font-sans text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span data-testid="checkout-subtotal">{formatPrice(subtotal)}</span>
                  </div>

                  {/* Discount Code Input */}
                  {!appliedDiscount ? (
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          data-testid="checkout-discount-input"
                          value={discountCode}
                          onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                          placeholder="Discount code"
                          className="pl-9 font-mono uppercase text-sm"
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleApplyDiscount())}
                        />
                      </div>
                      <Button variant="outline" size="sm" onClick={handleApplyDiscount} disabled={applyingDiscount}
                        data-testid="checkout-apply-discount-btn" className="text-xs">
                        {applyingDiscount ? '...' : 'Apply'}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2" data-testid="checkout-discount-applied">
                      <div className="flex items-center gap-2">
                        <Tag size={14} className="text-green-600" />
                        <span className="font-mono text-sm font-medium text-green-800">{appliedDiscount.code}</span>
                        <span className="text-xs text-green-600">{appliedDiscount.label}</span>
                      </div>
                      <button onClick={handleRemoveDiscount} className="text-green-600 hover:text-green-800" data-testid="checkout-remove-discount-btn">
                        <XIcon size={14} />
                      </button>
                    </div>
                  )}

                  {appliedDiscount && (
                    <div className="flex justify-between font-sans text-sm text-green-600">
                      <span>Discount</span>
                      <span data-testid="checkout-discount-amount">-{formatPrice(discountAmount)}</span>
                    </div>
                  )}

                  <div className="flex justify-between font-sans text-sm">
                    <span className="text-muted-foreground">Shipping ({isIndia ? 'India' : formData.country || 'International'})</span>
                    <span data-testid="checkout-shipping">{formatPrice(shippingCost)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Truck size={12} />
                    <span>Est. delivery: {region?.delivery}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="font-serif text-xl">Total</span>
                    <span className="font-serif text-2xl font-medium" data-testid="checkout-total">{formatPrice(total)}</span>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isProcessing}
                  data-testid="checkout-pay-button"
                  className="w-full bg-accent hover:bg-accent/90 mt-6"
                >
                  <CreditCard size={16} className="mr-2" />
                  {isProcessing ? 'Processing...' : `Pay ${formatPrice(total)}`}
                </Button>

                <p className="text-xs text-muted-foreground text-center mt-4">
                  Secure payment powered by Razorpay
                </p>
              </div>
            </div>
          </div>
        </form>
        </>
        )}
      </div>
    </div>
  );
};

export default Checkout;
